'use client'

import React, { useState } from 'react'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { getFetch, httpLink, loggerLink } from '@trpc/client'
import superjson from 'superjson'

import { useErrorToast } from '~/components/error-toast'
import { trpc } from './trpc-react'

const constructQueryClient = (
  tenantTheme: string,
  triggerErrorToast: () => void,
) => {
  const staleTime = 1000 * 60 * 60 // 1 hour
  const defaultOptions = {
    queries: {
      staleTime,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  }

  const handleError = (error: any, query: any) => {
    const isFailedBackgroundUpdate = query.state.data !== undefined
    const pagePath = window.location.pathname.split('/').pop()
    const serverErrorPageUrl = 'application-error'
    const isOnErrorPage = pagePath === serverErrorPageUrl
    const isOnCartPage = pagePath === 'shopping-cart'

    // Skip handling for specific pages
    if (isOnErrorPage || isOnCartPage) return

    if (isFailedBackgroundUpdate) {
      triggerErrorToast()
      console.log(
        `Background error: (${query?.state?.error?.data?.httpStatus}) ${error?.message}`,
      )
    } else {
      console.log(
        `Page load error: (${query?.state?.error?.data?.httpStatus}) ${
          error?.message
        }, ${pagePath}, ${JSON.stringify(query)}`,
      )
      window.location.href = `/${serverErrorPageUrl}?statusCode=${
        query?.state?.error?.data?.httpStatus ?? 500
      }`
    }
  }

  // Use a custom QueryCache only for the "maple" theme
  const queryCache =
    tenantTheme === 'maple'
      ? new QueryCache({ onError: handleError })
      : undefined

  return new QueryClient({
    defaultOptions,
    queryCache,
  })
}

export const TrpcProvider: React.FC<{
  children: React.ReactNode
  tenantTheme: string
}> = ({ children, tenantTheme }) => {
  const { triggerErrorToast } = useErrorToast()
  const [queryClient] = useState(() =>
    constructQueryClient(tenantTheme, triggerErrorToast),
  )

  const getBaseUrl = () => {
    if (typeof window !== 'undefined') return '' // browser should use relative url
    if (process.env.NEXT_PUBLIC_URL)
      return `https://${process.env.NEXT_PUBLIC_URL}` // SSR should use vercel url

    return `http://localhost:${process.env.PORT ?? 3000}` // dev SSR should use localhost
  }

  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        loggerLink({
          enabled: (opts) =>
            process.env.NODE_ENV === 'development' ||
            (opts.direction === 'down' && opts.result instanceof Error),
        }),
        httpLink({
          url: `${getBaseUrl()}/api/trpc`,
          fetch: async (input, init?) => {
            const fetch = getFetch()
            return fetch(input, {
              ...init,
              credentials: 'include',
            })
          },
        }),
      ],
      transformer: superjson,
    }),
  )
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  )
}
